import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import RaisedButton from 'material-ui/RaisedButton';
import ChangePasswordIcon from 'material-ui/svg-icons/notification/enhanced-encryption';
import ChangeEmail from 'material-ui/svg-icons/communication/email';
import Field from '../../../components/Field';
import FieldProfileImage from '../../../components/FieldProfileImage';
import noty from '../../../lib/noty';
import feathers from '../../../feathers';
import SubscriptionStatus from '../../../components/SubscriptionStatus';
import { checkAccountEditPermission } from '../../../components/Utilities';
import { Panel } from '../../../components/Panel';

export default class CurrentUser extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentUser: this.props.currentUser,
      fetching: false,
      resetPasswordFetching: false,
      resetEmailFetching: false,
      touched: false
    };
  }

  handleChange = ({ name, value }) => {
    const path = name.split('.');
    this.setState({
      touched: true,
      currentUser: this.state.currentUser.setIn(path, value)
    });
  };

  handleSubmit = event => {
    event.preventDefault();
    const { currentUser } = this.state;
    this.setState({ fetching: true });
    currentUser
      .patch()
      .then(patchedUser => {
        this.setState({
          fetching: false,
          touched: false
        });
        noty.success('Profile updated.');
      })
      .catch(err => {
        this.setState({ fetching: false });
        noty.errors(err);
      });
  };

  handleResetPassword(event) {
    event.preventDefault();
    const { currentUser } = this.state;
    noty.confirm(
      `Are you sure you want to change your password? An email will be sent to ${
        currentUser.email
      } to confirm this change.`,
      {
        onOk: () => {
          this.setState({ resetPasswordFetching: true });
          feathers
            .service('auth/password-forgot')
            .create({ email: currentUser.email })
            .then(resp => {
              noty.success(resp.message, { timeout: 20000 });
              this.setState({ resetPasswordFetching: false });
            })
            .catch(err => {
              noty.errors(err);
              this.setState({ resetPasswordFetching: false });
            });
        }
      }
    );
  }

  handleResetEmail(event) {
    event.preventDefault();
    const { currentUser } = this.state;
    noty.confirm(
      `Are you sure you want to change your email? An email will be sent to ${
        currentUser.email
      } to confirm this change.`,
      {
        onOk: () => {
          this.setState({ resetEmailFetching: true });
          feathers
            .service('auth/email-reset')
            // note the empty obj. A `data` obj must be sent on
            // the create method, even though it is not used,
            // feathers requires param `data` obj
            .create({})
            .then(resp => {
              noty.success(resp.message, { timeout: 20000 });
              this.setState({ resetEmailFetching: false });
            })
            .catch(err => {
              noty.errors(err);
              this.setState({ resetEmailFetching: false });
            });
        }
      }
    );
  }

  renderAccountEditorExpiredNotice() {
    return (
      <React.Fragment>
        <p style={{ fontWeight: 'bold', marginTop: '10px' }}>
          Your free trial has ended. Please{' '}
          <Link to="/account/settings">add a credit card</Link> to continue
          using MapPlug.
        </p>
        <p>
          If you need anything, please use the chat below or email us at{' '}
          <a href="mailto:info@mapplug.com">info@mapplug.com</a>
        </p>
      </React.Fragment>
    );
  }

  renderNonEditorExpiredNotice() {
    const { ownerName } = this.props;
    return (
      <React.Fragment>
        <p style={{ fontWeight: 'bold', marginTop: '10px' }}>
          Your free trial has ended.
        </p>
        <p>
          To get access again, tell your account owner, {ownerName}, to set up
          billing to restore the system for you.
        </p>
        <p>
          If you need anything, please use the chat below or email us at{' '}
          <a href="mailto:info@mapplug.com">info@mapplug.com</a>
        </p>
      </React.Fragment>
    );
  }

  render() {
    const {
      currentUser,
      fetching,
      touched,
      resetEmailFetching,
      resetPasswordFetching
    } = this.state;
    const { permissions, subscriptionStatus } = this.props;
    let { active } = subscriptionStatus;
    const accountEditPermission = checkAccountEditPermission({
      permissions,
      currentUser
    });
    return (
      <div className="margin-bottom-30">
        <Panel
          onSaveClick={this.handleSubmit}
          saveTitle="Save Profile"
          touched={touched}
          fetching={fetching}
          touchedMessage="Are you sure you want to leave? Your profile has unsaved changes."
        >
          <div>
            <form onSubmit={this.handleSubmit}>
              {!active ? (
                accountEditPermission ? (
                  <React.Fragment>
                    {this.renderAccountEditorExpiredNotice()}
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    {this.renderNonEditorExpiredNotice()}
                  </React.Fragment>
                )
              ) : (
                <div className="row">
                  <div className="col-xs-12">
                    <FieldProfileImage
                      name="image"
                      value={currentUser.image}
                      onChange={this.handleChange}
                    />
                  </div>
                </div>
              )}
              <div className="row">
                <div className="col-xs-6">
                  <Field
                    label="First Name"
                    name="first_name"
                    value={currentUser.first_name}
                    onChange={this.handleChange}
                    disabled={fetching}
                  />
                </div>
                <div className="col-xs-6">
                  <Field
                    label="Last Name"
                    name="last_name"
                    value={currentUser.last_name}
                    onChange={this.handleChange}
                    disabled={fetching}
                  />
                </div>
              </div>
              <div className="row margin-bottom-30">
                <div className="col-xs-12">
                  <Field
                    label="Email"
                    name="email"
                    type="email"
                    value={currentUser.email}
                    onChange={this.handleChange}
                    disabled={true}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-xs">
                  <RaisedButton
                    label="Reset Password"
                    onClick={this.handleResetPassword}
                    disabled={fetching || resetPasswordFetching}
                    icon={<ChangePasswordIcon />}
                    style={{ marginRight: 16 }}
                  />
                  <RaisedButton
                    label="Change Email"
                    onClick={this.handleResetEmail}
                    disabled={fetching || resetEmailFetching}
                    icon={<ChangeEmail />}
                  />
                </div>
              </div>
            </form>
          </div>
        </Panel>
      </div>
    );
  }
}

CurrentUser.propTypes = {
  currentUser: PropTypes.object.isRequired,
  permissions: PropTypes.object.isRequired,
  subscriptionStatus: PropTypes.instanceOf(SubscriptionStatus),
  ownerName: PropTypes.string.isRequired
};



// WEBPACK FOOTER //
// ./src/pages/Account/CurrentUser/index.jsx